import * as logger from "../utils/logger";
import { handleApi } from "../utils/error";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import UserHosRuleSet, { IUserHosRuleSet } from "../model/UserHosRuleSet";
import * as common from "./common";
import { getServerDate } from "../utils/storage/date";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { extend, isEqual, hashToArray } from "../utils/vanilla";
import { dateTimeComparator } from "../utils/date";
import BaseSync, { ISyncAddSet } from "./base";
import { IModifiedType } from "./sync.types";
import { DataActions } from "../store/data/actions";
import { getELDJurisdiction } from "hos-tools";
import {UserHosRuleSetActions } from "../store/userHosRuleSet/actions";
import {convertStorageRulesetsToDictionary} from "../utils/helpers";
import { FeedTypeKey } from "./common";
import { isDuplicateException, isOverLimitException } from "../utils/errorHandler/error.types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";

export default class UserHosRuleSetSync extends BaseSync {
    private feedVersion;
    private lastRuleSetName: string;

    getLogs(apiUser: IUser | null): IIdDictionary<UserHosRuleSet> {
        let ruleSets = apiUser && this.store.getState().userHosRuleSets[apiUser?.id];
        return convertStorageRulesetsToDictionary(ruleSets);
    }

    setLogs(logsToSave: IIdDictionary<UserHosRuleSet>, apiUser: IUser | null) {
        apiUser && this.store.dispatch(UserHosRuleSetActions.setUserHosRuleSets({ userId: apiUser.id, userHosRuleSets: logsToSave }));
    }

    //TODO: Need to make this method generic and share it between other sync code
    syncLogs(serverLogs: UserHosRuleSet[]) {
        return new Promise((resolve, reject) => {
            let currentUser = this.api.getUser();
            let localLogs: IIdDictionary<UserHosRuleSet> = this.getLogs(currentUser);
            let calls: [IModifiedType, ISyncAddSet<IUserHosRuleSet>][] = [];

            // Merge in server logs with local logs
            serverLogs.forEach((serverLog: UserHosRuleSet) => {
                let server = new UserHosRuleSet(serverLog);
                let local = localLogs[serverLog.id];
                // Server log exists locally
                if (local) {
                    // Conflict resolution
                    let previousState = local.previousState ? new UserHosRuleSet(local.previousState) : undefined;
                    let modifiedState = local.modified;

                    /*
                    Four scenarios:
                    local changed, server same => take local
                    local changed, server changed => conflict if local was "edit", merge local and server if local was "add"
                    local same, server changed => take server
                    local same, server same => do nothing
                    */

                    if (modifiedState) {
                        //Local changed
                        if (!isEqual(previousState, server)) {
                            if (modifiedState === "Add") {
                                // Server has a different version, merge them
                                logger.log(logger.Source.SYNC, "Merged local UserHosRuleSet with server", currentUser);
                                localLogs[serverLog.id] = common.merge<UserHosRuleSet>(localLogs[serverLog.id], server, { modified: "Set" });
                            } else {
                                logger.error(logger.Source.SYNC, `SYNC: UserHosRuleSet conflict (both server and client changed). Local ID: ${ local.id }, Server ID: ${ server.id }`);
                            }
                        }
                    } else {
                        // Local same
                        if (!isEqual(local, server)) {
                            logger.log(logger.Source.SYNC, "Updated local UserHosRuleSet from server", currentUser);
                            localLogs[serverLog.id] = serverLog;
                        }
                    }
                } else {
                    localLogs[serverLog.id] = serverLog;
                }
            });

            // Prepare local logs for push to server
            Object.keys(localLogs).forEach((id: string) => {
                let log = localLogs[id];

                if (!log.id) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                if (log.modified) {
                    let sanitizedLog: IUserHosRuleSet = extend({}, log);
                    this.removeClientSideProperties(sanitizedLog);

                    if (log.modified === "Add" || log.modified === "Set") {
                        if (log._syncCounter === undefined) {
                            log._syncCounter = 5;
                        }
                        if (log._syncCounter > 0) {
                            log._syncCounter--;
                            calls.push([log.modified, { typeName: "UserHosRuleSet", entity: sanitizedLog }]);
                        }
                    }
                }
            });

            //Check if rulesets are out of sync and a forced sync is required
            let shouldHosForceSync = false;
            if (currentUser && localLogs && Object.keys(localLogs).length) {
                //Get the ruleset name from the local logs
                let currentUserRules = hashToArray<UserHosRuleSet>(localLogs)
                    .filter(rule => rule.user.id === currentUser?.id)
                    .sort(dateTimeComparator());
                let localLogsRuleSetNameRegex = currentUserRules[currentUserRules.length - 1].hosRuleSet.id.match(/HosRuleSet([^"]+)/);
                let localLogsRuleSetName = localLogsRuleSetNameRegex ? localLogsRuleSetNameRegex[1] : "None";

                if (!this.lastRuleSetName) {
                    this.lastRuleSetName = localLogsRuleSetName;
                }

                // Should full sync HOS if the ruleset changed, to ensure the logs are correct. But we don't want to do this if it's the first sync.
                // The reason for not checking the api user directly is because it is possible that the api user is already updated (since the user object is updated at the same time due to pubsubs)
                if (this.lastRuleSetName && this.lastRuleSetName !== localLogsRuleSetName) {
                    let oldRetentionPeriod = this.hosProcess.getLogRetentionDays(this.lastRuleSetName);
                    let newRetentionPeriod = this.hosProcess.getLogRetentionDays(localLogsRuleSetName);

                    if (oldRetentionPeriod !== newRetentionPeriod) {
                        shouldHosForceSync = true;
                    }
                    this.lastRuleSetName = localLogsRuleSetName;
                }
            }

            if (calls.length > 0) {
                logger.log(logger.Source.SYNC, common.formatCallForLogger("UserHosRuleSet", calls), currentUser);
                this.api.multiCall(calls, (results) => {
                    let getLocalEntity = (entity: IUserHosRuleSet) => localLogs[entity.id];
                    this.resolveSyncedEntities(calls, results, getLocalEntity);

                    // Update localStorage
                    resolve([localLogs, shouldHosForceSync]);
                }, errorObject => {
                    if (isOverLimitException(errorObject)) {
                        reject(errorObject);
                        return;
                    }
                    let requestIndex = errorObject.data.requestIndex;
                    let call = calls[requestIndex];
                    let log = call && call[1].entity;
                    let hasRulesetLocally = !!log && localLogs.hasOwnProperty(log.id);

                    if (isDuplicateException(errorObject) && hasRulesetLocally) {
                        // This log is already in the database, make it a "Set" and sync again
                        localLogs[log.id].modified = "Set";
                        this.setLogs(localLogs, this.api.getUser());
                        this.syncLogs(serverLogs).then(resolve, reject);
                    } else {
                        dispatcher.trigger(DispatcherEvents.SyncUserHosRuleSet);
                        reject(errorObject);
                    }
                });
            } else {
                resolve([localLogs, shouldHosForceSync]);
            }
        });
    }

    fetchData () {
        return new Promise<UserHosRuleSet[]>((resolve, reject) => {
            let currentUser = this.api.getUser();
            let userHosRulesets = this.getLogs(currentUser);
            let method;
            let query = {
                typeName: "UserHosRuleSet",
                search: {
                    userSearch: { id: currentUser?.id },
                    fromDate: null as (string | null)
                },
                fromVersion: <any>null
            };
            let currentVersion = common.getFeedVersion(this.api, this.store, FeedTypeKey.USER_HOS_RULESET);
            let isFeed = userHosRulesets && Object.keys(userHosRulesets).length > 0 && !!currentVersion;

            if (isFeed) {
                method = "GetFeed";
                query.fromVersion = currentVersion;
            } else {
                method = "Get";
                delete query.fromVersion;

                // Get all of them from ~month ago
                let fromDate = getServerDate();
                fromDate.setMonth(fromDate.getMonth() - 1);
                query.search.fromDate = fromDate.toISOString();
            }

            this.api.call(method, query, data => {
                let sets = isFeed ? data.data || [] : data || [];
                logger.log(logger.Source.SYNC, `Downloaded ${ sets.length } UserHosRuleSet(s)`, currentUser);

                // Need to store the version.
                if (isFeed) {
                    this.feedVersion = data.toVersion;
                } else {
                    // If using get, sort logs by version and store the latest version.
                    if (sets.length > 0) {
                        let logsByVersion = sets.sort((a, b) => parseInt(a.version, 16) < parseInt(b.version, 16) ? -1 : 1);
                        let latestLogByVersion = logsByVersion[logsByVersion.length - 1];

                        this.feedVersion = latestLogByVersion.version;
                    }
                }
                resolve(sets);
            }, (err) => {
                handleApi(err);
                reject(err);
            });
        });
    }

    setFeedVersion(version) {
        common.setFeedVersion(this.api, this.store, FeedTypeKey.USER_HOS_RULESET, version);
    }

    synchronize(progressCb: common.TSyncCallback, profilingBatcher?: PerformanceProfileBatcher): Promise {
        return this.createProfiledSync(MeasureName.SyncUserHosRuleSet, () => {
            return new Promise(resolve => {
                this.fetchData()
                .then(sets => this.syncLogs(sets))
                .then(([localLogs, shouldHosForceSync]) => {
                    let apiUser = this.api.getUser();
                    if (apiUser) {
                        this.setLogs(localLogs, apiUser);
                    }

                    // Update the ELD Jurisdiction
                    let ruleSetName = this.hosProcess.getLatestHosRuleSetIdShortened(true);
                    this.store.dispatch(DataActions.setELDJurisdiction(getELDJurisdiction(ruleSetName)));

                    dispatcher.trigger(DispatcherEvents.SyncUserHosRuleSet);
                    this.setFeedVersion(this.feedVersion);
                    progressCb("UserHosRuleSet");
                    resolve(shouldHosForceSync);
                })
                .catch(err => {
                    progressCb("UserHosRuleSet", err);
                    resolve(false);
                    handleApi(err);
                });
            });
        }, profilingBatcher);
    }
}